/**
 * Close Out guard: "N reports, 0 tests" (11/2/2026).
 *
 * A job with official test reports but no test records is NOT "$0, nothing to
 * bill" and is NOT ready to send: the tests were never made, so the device's
 * due date never moves and nothing is billed (FWISD Daggett #102678782).
 * This panel takes the place of "Send packet", "Already sent another way" and
 * "No invoice needed" in that case, and points to paper conversion. The server
 * refuses the same sends (409 REPORTS_WITHOUT_TESTS), so this is not the lock,
 * only the honest face of it.
 */
import { FileWarning } from "lucide-react";
import { Link } from "react-router-dom";

export interface ReportsWithoutTests {
    reports: number;
    tests: number;
    blocked: boolean;
}

export function reportsWithoutTestsLine(reports: number): string {
    return `${reports} report${reports === 1 ? "" : "s"}, 0 tests — make the tests from these reports`;
}

export function ReportsWithoutTestsGuard({ jobId, reports }: { jobId: string; reports: number }) {
    return (
        <div
            data-testid="closeout-reports-without-tests"
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 p-3 dark:border-red-500/50 dark:bg-red-950/40"
        >
            <p className="flex items-start gap-2 text-[13px] font-bold text-red-900 dark:text-red-200">
                <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
                {reportsWithoutTestsLine(reports)}
            </p>
            <p className="mt-1 text-[12px] text-red-900/80 dark:text-red-200/80">
                The reports are here, but no test was saved, so the due date won&apos;t move and nothing gets billed.
                Open the job and use the signed paper on each device. Then come back here to send.
            </p>
            <Link
                to={`/jobs/${jobId}?paper=1`}
                data-testid="closeout-make-tests-link"
                className="mt-2 inline-flex h-8 items-center rounded-md bg-red-700 px-3 text-[12px] font-semibold text-white hover:bg-red-800"
            >
                Make the tests from the reports
            </Link>
        </div>
    );
}
